import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import MainWrapper from 'layouts/MainWrapper'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { ERC721_MIN_ABI, MARKETPLACE_ABI, MASTER_DISTRIBUTOR_MIN_ABI } from 'utils/marketplace/abi'
import { decodeMarketplaceError } from 'utils/marketplace/errorMessages'
import { readOwnedLicenceTokenIds } from 'utils/marketplace/readOwnedLicences'
import { useUnclaimedForToken } from 'utils/marketplace/useUnclaimedForToken'
import TxConfirmation from 'components/TxConfirmation'
import useToast from 'hooks/useToast'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { useLicenceNamesByToken } from 'hooks/useLicenceNamesByToken'
import ExpiryPicker from './components/ExpiryPicker'

/** Same select the DAO form and the marketplace toolbar use. */
const Select = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

/**
 * A form is read down a column, not across a monitor. Full-width fields on a
 * wide screen put the label and its input at opposite ends of the eye's
 * travel, which is why this is capped and centred rather than filling the
 * page like a table would.
 */
const FormColumn = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const NewListingForm: React.FC = () => {
  // The route may carry a collection and a token, only a collection, or
  // neither — the marketplace's own call to action arrives with neither,
  // because a wallet can hold licences in more than one collection and
  // guessing which one the seller means would be wrong.
  const { nft: nftParam, tokenId: tokenIdParam } = useParams<{
    nft: string
    tokenId: string
  }>()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { account, provider } = useWeb3React()
  const ctx = useMarketplaceContext()
  /**
   * Every signing action here must run on Base.
   *
   * Nothing in the marketplace enforced the chain, so an approval signed
   * while the wallet sat on Ethereum went to an address that holds no
   * contract there — MetaMask showed 'no changes' and the listing could
   * never proceed. Every other contract flow in the app already does this.
   */
  const ensureChainForTx = useSwitchChainForContractTx()
  /**
   * A signer bound to Base, not to whatever chain the wallet is sitting on.
   *
   * Switching the wallet is not enough: the provider captured in this
   * render still points at the old chain, so the transaction went out on
   * Ethereum even after a successful switch. Every other contract flow in
   * the app resolves its signer by chain id for exactly this reason.
   */
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const { toastSuccess, toastError } = useToast()
  // A seller recognises the name they gave a licence, not its token id.
  const licenceNames = useLicenceNamesByToken()

  // Chosen in the form when the route did not name one.
  const [selectedNft, setSelectedNft] = useState<string>((nftParam ?? '').toLowerCase())
  const nft = selectedNft

  // The route may name a token, or only a collection: the licences table
  // names the token because the row knows it, while the marketplace's own
  // call to action cannot. When it is absent the seller picks from the tokens
  // the wallet actually holds, read from the collection itself.
  const [selectedTokenId, setSelectedTokenId] = useState<string>(tokenIdParam ?? '')
  const [ownedTokenIds, setOwnedTokenIds] = useState<string[] | null>(null)
  const [txHash, setTxHash] = useState<string | null>(null)
  const [createdListingId, setCreatedListingId] = useState<string | null>(null)
  /**
   * What the seller actually receives. The contract takes its fee out of the
   * proceeds — the buyer pays exactly the listed price — so someone typing
   * 10 000 needs to see the 9 000 before signing, not discover it at
   * settlement. Read from the contract, so it follows the real rate.
   */
  const [quote, setQuote] = useState<{
    fee: string
    /** Marketplace fee as a percentage, so the copy follows the real rate. */
    feePercent: string
    proceeds: string
    /** What MPRO burns on the way to the seller. Zero while the rate is zero. */
    burn: string
    /** Proceeds after the burn — what actually lands in the wallet. */
    net: string
  } | null>(null)
  const tokenId = selectedTokenId
  // A seller must see what leaves with the licence before naming a price:
  // it is routinely worth more than the asking price.
  const unclaimed = useUnclaimedForToken(nft, tokenId || undefined)

  const [priceMpro, setPriceMpro] = useState('')
  /** Local datetime string; empty = the listing never expires. */
  const [expiry, setExpiry] = useState('')
  const [busy, setBusy] = useState<'approve' | 'list' | null>(null)
  const [hasApproval, setHasApproval] = useState<boolean | null>(null)
  const [tokenOwner, setTokenOwner] = useState<string | null>(null)
  /**
   * Whether the marketplace contract itself accepts this collection. The
   * allowlist belongs to the contract's owner — the operator today, the DAO
   * later — so the platform's own list of licence contracts is a label
   * source, not the authority. Asking the contract stops a user paying gas
   * for a listing it would reject.
   */
  const [contractAllows, setContractAllows] = useState<boolean | null>(null)

  const collectionLabel = useMemo(
    () => ctx.collections.find((c) => c.nft === nft)?.label ?? `${nft.slice(0, 6)}…${nft.slice(-4)}`,
    [ctx.collections, nft]
  )

  const inAllowlist = useMemo(() => {
    // The contract is the authority; fall back to the platform's list only
    // while the on-chain answer has not arrived (or could not be read).
    if (contractAllows !== null) return contractAllows
    return ctx.collections.some((c) => c.nft === nft)
  }, [contractAllows, ctx.collections, nft])

  // Pre-flight: read current owner + approval state.
  const refreshState = useCallback(async () => {
    if (!ctx.marketplaceAddress || !nft || !tokenId) {
      // No token, no verdict — and drop any answer left from a previous one.
      setTokenOwner(null)
      setHasApproval(null)
      return
    }
    // The answers below belong to the token being read. Until they arrive,
    // the previous token's answers are not just stale, they are about a
    // different licence.
    setTokenOwner(null)
    setHasApproval(null)
    try {
      const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
      const erc721 = new Contract(nft, ERC721_MIN_ABI as unknown as ethers.ContractInterface, rpc)
      const market = new Contract(ctx.marketplaceAddress, MARKETPLACE_ABI as unknown as ethers.ContractInterface, rpc)
      const [owner, perToken, perCollection, allowed] = await Promise.all([
        erc721.ownerOf(tokenId).catch(() => ethers.constants.AddressZero),
        erc721.getApproved(tokenId).catch(() => ethers.constants.AddressZero),
        account ? erc721.isApprovedForAll(account, ctx.marketplaceAddress).catch(() => false) : Promise.resolve(false),
        market.allowedCollection(nft).catch(() => null)
      ])
      setContractAllows(allowed === null ? null : Boolean(allowed))
      setTokenOwner(String(owner).toLowerCase())
      setHasApproval(Boolean(perCollection) || String(perToken).toLowerCase() === ctx.marketplaceAddress.toLowerCase())
    } catch {
      setHasApproval(null)
      setTokenOwner(null)
      setContractAllows(null)
    }
  }, [ctx.marketplaceAddress, ctx.chainId, nft, tokenId, account])

  useEffect(() => {
    void refreshState()
  }, [refreshState])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!ctx.marketplaceAddress || !priceMpro.trim()) {
        setQuote(null)
        return
      }
      let parsed: ethers.BigNumber
      try {
        parsed = ethers.utils.parseEther(priceMpro.trim())
      } catch {
        setQuote(null)
        return
      }
      if (parsed.lte(0)) {
        setQuote(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
        const market = new Contract(ctx.marketplaceAddress, MARKETPLACE_ABI as unknown as ethers.ContractInterface, rpc)
        const [fee, proceeds] = await market.quote(parsed)
        const feeBps: number = await market.feeBps()

        // MPRO burns a share of every transfer when its rate is non-zero, and
        // the rate is per sender. Asking the distributor is the only way to
        // tell this seller what they will actually receive; a hardcoded
        // percentage would be wrong the day either number changes.
        let burn = ethers.BigNumber.from(0)
        try {
          const distributor = new Contract(
            ctx.masterDistributorAddress,
            MASTER_DISTRIBUTOR_MIN_ABI as unknown as ethers.ContractInterface,
            rpc
          )
          burn = await distributor.getBurnAmount(account ?? nft, proceeds)
        } catch {
          // Unreadable burn is reported as zero rather than guessed.
        }

        if (cancelled) return
        setQuote({
          fee: ethers.utils.formatEther(fee),
          feePercent: (Number(feeBps) / 100).toString(),
          proceeds: ethers.utils.formatEther(proceeds),
          burn: ethers.utils.formatEther(burn),
          net: ethers.utils.formatEther(proceeds.sub(burn))
        })
      } catch {
        if (!cancelled) setQuote(null)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [priceMpro, ctx.marketplaceAddress, ctx.chainId])

  // Which licences of this collection the wallet holds.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!account || !nft) {
        setOwnedTokenIds(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
        const ids = await readOwnedLicenceTokenIds({
          nft,
          owner: account,
          provider: rpc
        })
        if (cancelled) return
        setOwnedTokenIds(ids)
        // With exactly one licence there is nothing to choose.
        if (!tokenIdParam && ids.length === 1) setSelectedTokenId(ids[0])
      } catch {
        if (!cancelled) setOwnedTokenIds([])
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [account, nft, ctx.chainId, tokenIdParam])

  const isOwner = !!account && !!tokenOwner && tokenOwner === account.toLowerCase()

  const onApprove = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('approve')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const erc721 = new Contract(nft, ERC721_MIN_ABI as unknown as ethers.ContractInterface, signer)
      const tx = await erc721.setApprovalForAll(ctx.marketplaceAddress, true)
      await tx.wait()
      toastSuccess(t('marketplace.approveCollectionOk', { collection: collectionLabel }))
      await refreshState()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [provider, account, ctx.marketplaceAddress, nft, collectionLabel, refreshState, toastError, toastSuccess, t])

  const expiryTs = useMemo(() => {
    if (!expiry) return 0n
    const ts = Math.floor(new Date(expiry).getTime() / 1000)
    return Number.isFinite(ts) && ts > 0 ? BigInt(ts) : 0n
  }, [expiry])
  // Client-side courtesy for the contract's MIN_EXPIRY_LEAD, so the wallet
  // never opens for a transaction that must revert. The contract stays the
  // authority.
  const expiryTooSoon = useMemo(() => expiryTs !== 0n && Number(expiryTs) * 1000 < Date.now() + 3600_000, [expiryTs])

  const onList = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress) return
    let parsed: ethers.BigNumber
    try {
      parsed = ethers.utils.parseEther(priceMpro.trim() || '0')
    } catch {
      toastError(t('marketplace.invalidPrice'))
      return
    }
    if (parsed.lte(0)) {
      toastError(t('marketplace.invalidPrice'))
      return
    }
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('list')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      // Without an expiry the pre-V2 signature is used — it works on both
      // the deployed V1 and on V2, so this form never depends on the upgrade
      // having happened. The 4-arg overload exists only on V2.
      const tx =
        expiryTs === 0n
          ? await market['list(address,uint256,uint256)'](nft, tokenId, parsed)
          : await market['list(address,uint256,uint256,uint64)'](nft, tokenId, parsed, expiryTs)
      setTxHash(tx.hash)
      const receipt = await tx.wait()
      // Find Listed event for the new listingId.
      const listedEvent = receipt.events?.find((e: { event?: string }) => e.event === 'Listed')
      const listingId = listedEvent?.args?.listingId?.toString()
      toastSuccess(
        t('marketplace.listOkDetailed', {
          collection: collectionLabel,
          tokenId,
          price: priceMpro
        })
      )
      // Deliberately not navigating away: the transaction hash is the user's
      // proof the listing was created, and a redirect would take it off screen
      // before they could read it. The confirmation links on to the listing.
      setCreatedListingId(listingId ?? null)
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    ctx.marketplaceAddress,
    nft,
    tokenId,
    priceMpro,
    collectionLabel,
    navigate,
    toastError,
    toastSuccess,
    t,
    expiryTs
  ])

  if (!ctx.marketplaceAddress) {
    return (
      <MainWrapper>
        <Flex fullWidth py="40px">
          <Text>{t('marketplace.notConfigured')}</Text>
        </Flex>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px" alignItems="center">
        <FormColumn>
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace')}>
            ← {t('marketplace.backToList')}
          </Button>

          <Text variant={TextVariant.H4}>
            {tokenId
              ? t('marketplace.listTitle', {
                  collection: collectionLabel,
                  tokenId
                })
              : nft
                ? t('marketplace.listTitleNoToken', { collection: collectionLabel })
                : t('marketplace.listTitleNoCollection')}
          </Text>

          {/* Nothing to say about an allowlist before a collection is chosen. */}
          {nft && !inAllowlist ? (
            <Section>
              <Text color={colors.red}>{t('marketplace.notAllowed')}</Text>
            </Section>
          ) : null}

          {txHash ? (
            <Section>
              <TxConfirmation hash={txHash} chainId={ctx.chainId} title={t('marketplace.listedTitle')} />
              <Flex gap="10px" flexWrap="wrap">
                {createdListingId ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/marketplace/listings/${createdListingId}`)}
                  >
                    {t('marketplace.viewListing')}
                  </Button>
                ) : null}
                <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace')}>
                  {t('marketplace.backToList')}
                </Button>
              </Flex>
            </Section>
          ) : null}

          {/*
          What is being sold. The route may name a collection (from the
          licences table) or nothing at all (from the marketplace's call to
          action); either way no licence record carries a token id, so the
          licence itself is chosen here from what the wallet demonstrably
          holds on chain.
        */}
          {!nftParam && !txHash ? (
            <Section>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.chooseCollection')}</Text>
              <Select
                value={selectedNft}
                onChange={(e) => {
                  setSelectedNft(e.target.value)
                  setSelectedTokenId('')
                  setOwnedTokenIds(null)
                }}
              >
                <option value="">{t('marketplace.chooseCollectionPlaceholder')}</option>
                {ctx.collections.map((c) => (
                  <option key={c.nft} value={c.nft}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Section>
          ) : null}

          {!tokenIdParam && !txHash && nft ? (
            <Section>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.chooseLicence')}</Text>
              {ownedTokenIds === null ? (
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  {t('marketplace.loadingLicences')}
                </Text>
              ) : ownedTokenIds.length === 0 ? (
                <Text color={colors.red}>{t('marketplace.noLicencesHere')}</Text>
              ) : (
                <Select value={selectedTokenId} onChange={(e) => setSelectedTokenId(e.target.value)}>
                  <option value="">{t('marketplace.chooseLicencePlaceholder')}</option>
                  {ownedTokenIds.map((id) => (
                    <option key={id} value={id}>
                      {licenceNames[id]
                        ? `${licenceNames[id]} · ${collectionLabel} #${id}`
                        : `${collectionLabel} #${id}`}
                    </option>
                  ))}
                </Select>
              )}
            </Section>
          ) : null}

          {/*
          Every line here is about ONE token, so the whole section waits for a
          licence to be chosen. Rendered earlier it accused the seller of not
          owning a token they had not yet picked — read as "your five licences
          are not yours".
        */}
          {nft && tokenId ? (
            <Section>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.preflight')}</Text>
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('marketplace.tokenOwner')}: {tokenOwner ? `${tokenOwner.slice(0, 8)}…${tokenOwner.slice(-6)}` : '—'}
              </Text>
              {/*
            An unread owner is not a verdict: until the chain answers, say
            nothing rather than accuse.
          */}
              {tokenOwner === null ? (
                <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                  {t('marketplace.checkingOwnership')}
                </Text>
              ) : !isOwner ? (
                <Text color={colors.red}>{t('marketplace.notTokenOwner')}</Text>
              ) : (
                <Text color={colors.green}>{t('marketplace.youOwnIt')}</Text>
              )}
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('marketplace.approvalStatus')}:{' '}
                {hasApproval === null
                  ? '—'
                  : hasApproval
                    ? t('marketplace.approvalGranted')
                    : t('marketplace.approvalMissing')}
              </Text>
              {isOwner && hasApproval === false ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={busy !== null}
                  onClick={() => void onApprove()}
                >
                  {busy === 'approve' ? t('marketplace.approving') : t('marketplace.approveCollection')}
                </Button>
              ) : null}
            </Section>
          ) : null}

          {nft ? (
            <Section>
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.priceLabel')}</Text>
              <Input
                placeholder={t('marketplace.pricePlaceholder')}
                value={priceMpro}
                onChange={(e) => setPriceMpro(e.target.value)}
                inputMode="decimal"
                disabled={!isOwner || hasApproval !== true}
              />
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('marketplace.priceHint')}
              </Text>
              {unclaimed.unclaimedMpro !== null && unclaimed.unclaimedMpro > 0 && (
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
                  {t('marketplace.sellCarriesUnclaimed', {
                    amount: unclaimed.unclaimedMpro.toLocaleString('en-US', {
                      maximumFractionDigits: 2
                    })
                  })}
                </Text>
              )}
              {quote ? (
                <Flex flexDirection="column" gap="2px">
                  <Text variant={TextVariant.BODY_SMALL_BOLD}>
                    {t('marketplace.youReceive', { amount: quote.net })}
                  </Text>
                  <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                    {t('marketplace.deductionsLine', {
                      feePercent: quote.feePercent,
                      fee: quote.fee,
                      burn: quote.burn
                    })}
                  </Text>
                </Flex>
              ) : null}
              <Text variant={TextVariant.BODY_SMALL_BOLD}>{t('marketplace.listingExpiry')}</Text>
              <ExpiryPicker value={expiry} onChange={setExpiry} disabled={!isOwner || hasApproval !== true} />
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('marketplace.offerExpiryHint')}
              </Text>
              {expiryTooSoon && (
                <Text variant={TextVariant.BODY_SMALL} color={colors.red}>
                  {t('marketplace.expiryTooSoon')}
                </Text>
              )}
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={
                  expiryTooSoon ||
                  !isOwner ||
                  hasApproval !== true ||
                  !inAllowlist ||
                  busy !== null ||
                  !priceMpro.trim()
                }
                onClick={() => void onList()}
              >
                {busy === 'list' ? t('marketplace.listing') : t('marketplace.listForSale')}
              </Button>
            </Section>
          ) : null}
        </FormColumn>
      </Flex>
    </MainWrapper>
  )
}

export default NewListingForm
